import { PointerIcon, ToolType, toolTypes} from '@readcloud/annotations';
import { Tooltip, TooltipContent, TooltipTrigger } from '@readcloud/ui';
import {
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from '@readcloud/component-library/components/Base/Button';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalTitle,
} from '@readcloud/component-library/components/Base/Modal';
import { Annotation } from '@readcloud/data';
import C from 'classnames';
import React from 'react';
import { IconOfAnnotation } from '../../Common/Annotations/IconOfAnnotation';
import { MdClose } from 'react-icons/md';
import useMediaQuery from '@readcloud/common/lib/useMediaQuery';

export function DrawToolbar({
  onDiscard,
  tool,
  onToolChange,
  confirmDiscard,
  addAnnotation,
  pageNumber,
  authorId,
  bookId,
  ...props
}: {
  confirmDiscard: boolean;
  onDiscard: () => void;
  tool: ToolType;
  onToolChange: (tool: ToolType) => void;
  addAnnotation: (anno: Annotation) => void;
  pageNumber: number;
  authorId: string;
  bookId: string;
  disableHTML?: boolean;
  disableMedia?: boolean;
  disableDrawing?: boolean;
  className?: string;
}) {
  const [confirmVisible, setConfirmVisible] = React.useState(false);

  const handleDiscard = React.useCallback(() => {
    if (confirmDiscard) {
      setConfirmVisible(true);
    } else {
      onDiscard();
    }
  }, [onDiscard, confirmDiscard]);

  const handleBack = React.useCallback(() => {
    setConfirmVisible(false);
  }, []);

  const handleConfirmDiscard = React.useCallback(() => {
    setConfirmVisible(false);
    onDiscard();
  }, [onDiscard]);

  const handleToolChange = React.useCallback(
    (val: string) => {
      onToolChange(val as ToolType);
    },
    [onToolChange]
  );

  const isLargeScreen = useMediaQuery('(min-width: 768px)');

  return (
    <>
      <Modal isOpen={confirmVisible}>
        <ModalTitle>Confirm Discard</ModalTitle>
        <ModalBody>
          <p>
            Your current drawing will be lost if you continue. Are you sure?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button outline onClick={handleBack}>
            Back
          </Button>
          <Button variant="danger" onClick={handleConfirmDiscard}>
            Discard
          </Button>
        </ModalFooter>
      </Modal>
      <div
        className={C(
          'rc-flex rc-justify-start rc-items-center rc-w-full',
          props.className
        )}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button icon variant="light" onClick={handleDiscard} title="Cancel">
              <MdClose size={isLargeScreen ? 28 : 16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Exit</TooltipContent>
        </Tooltip>
        <div className="rc-px-1" />

        <ToggleButtonGroup
          key={`toggle-group-${tool}`}
          className="rc-max-w-full rc-overflow-auto"
          onValueChange={handleToolChange}
          value={tool}
          type="single"
          ensureValue
        >
          {!props.disableDrawing && (
            <>
              <ToggleButton
                id={toolTypes.Select}
                type="button"
                name={toolTypes.Select}
                value={toolTypes.Select}
                key={toolTypes.Select}
                variant="light"
                className='rc-h-auto md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="draw-icon-container">
                      <PointerIcon size={isLargeScreen ? 24 : 16} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Select Tool</TooltipContent>
                </Tooltip>
              </ToggleButton>

              <ToggleButton
                id={toolTypes.Path}
                type="button"
                name={toolTypes.Path}
                value={toolTypes.Path}
                key={toolTypes.Path}
                className='rc-h-8 md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="draw-icon-container">
                      <IconOfAnnotation type="Path" size={isLargeScreen ? 28 : 16} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Free Hand</TooltipContent>
                </Tooltip>
              </ToggleButton>

              <ToggleButton
                id={toolTypes.Circle}
                type="button"
                name={toolTypes.Circle}
                value={toolTypes.Circle}
                key={toolTypes.Circle}
                variant="light"
                className='rc-h-auto md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="draw-icon-container">
                      <IconOfAnnotation className='' type="Circle" size={isLargeScreen ? 28 : 16} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Circle Highlight</TooltipContent>
                </Tooltip>
              </ToggleButton>

              <ToggleButton
                id={toolTypes.Rectangle}
                type="button"
                name={toolTypes.Rectangle}
                value={toolTypes.Rectangle}
                key={toolTypes.Rectangle}
                variant="light"
                className='rc-h-auto md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="draw-icon-container">
                      <IconOfAnnotation className='' type="Rectangle" size={isLargeScreen ? 28 : 16} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Rectangle Highlight</TooltipContent>
                </Tooltip>
              </ToggleButton>

              <ToggleButton
                id={toolTypes.OverlayText}
                type="button"
                name={toolTypes.OverlayText}
                value={toolTypes.OverlayText}
                key={toolTypes.OverlayText}
                variant="light"
                className='rc-h-8 md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="rc-flex rc-items-center rc-justify-center">
                      <IconOfAnnotation type="OverlayText" size={isLargeScreen ? 24 : 12}/>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Text</TooltipContent>
                </Tooltip>
              </ToggleButton>
            </>
          )}
          {!props.disableMedia && (
            <>
              <ToggleButton
                id={toolTypes.Media}
                type="button"
                name={toolTypes.Media}
                value={toolTypes.Media}
                key={toolTypes.Media}
                variant="light"
                className='rc-h-auto md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="draw-icon-container">
                      <IconOfAnnotation type="Media" size={isLargeScreen ? 28 : 16} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Media</TooltipContent>
                </Tooltip>
              </ToggleButton>

              <ToggleButton
                id={toolTypes.Image}
                type="button"
                name={toolTypes.Image}
                value={toolTypes.Image}
                key={toolTypes.Image}
                variant="light"
                className='rc-h-auto md:rc-h-9 md:rc-px-3 rc-p-2'
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="draw-icon-container">
                      <IconOfAnnotation type="Image" size={isLargeScreen ? 28 : 16} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Image</TooltipContent>
                </Tooltip>
              </ToggleButton>
            </>
          )}
          {!props.disableHTML && (
            <ToggleButton
              id={toolTypes.HTML}
              type="button"
              name={toolTypes.HTML}
              value={toolTypes.HTML}
              key={toolTypes.HTML}
              variant="light"
              className='rc-h-auto md:rc-h-9 md:rc-px-3 rc-p-2'
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="draw-icon-container">
                    <IconOfAnnotation type="HTML" size={isLargeScreen ? 28 : 16}/>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Rich Text</TooltipContent>
              </Tooltip>
            </ToggleButton>
          )}
        </ToggleButtonGroup>
      </div>
    </>
  );
}
